// SuperAdmin → Activities tab: global volume + top companies by activity.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EChart from '../charts/EChart';
import { ChartCard, DataTable, EmptyState, ExportButton, exportToCsv } from './shared';

const SuperActivities = ({ view, t, period }) => {
  const { activityTrends = [], topCompaniesActivity = [], topCompanies = [] } = view;
  const topRows = topCompaniesActivity.length ? topCompaniesActivity : topCompanies;

  const volumeOption = useMemo(() => {
    const hasData = activityTrends.some((r) => (r.login || r.create || r.update || r.delete));
    if (!activityTrends.length || !hasData) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', boundaryGap: false, data: activityTrends.map((r) => r.name) },
      yAxis: { type: 'value' },
      series: ['login', 'create', 'update', 'delete'].map((k) => ({
        name: k, type: 'line', stack: 'total', smooth: true, showSymbol: false,
        areaStyle: { opacity: 0.3 }, data: activityTrends.map((r) => r[k] || 0)
      }))
    };
  }, [activityTrends]);

  const columns = useMemo(() => ([
    { key: 'name', label: t('colCompany') },
    { key: 'activities', label: t('colActivities'), render: (r) => r.activities ?? r.value ?? '—' },
    { key: 'users', label: t('colUsers'), render: (r) => r.users ?? '—' }
  ]), [t]);

  return (
    <div className="space-y-4">
      <ChartCard title={t('volumeTitle')} subtitle={t('volumeSubtitle', { days: period })}>
        {volumeOption ? <EChart option={volumeOption} height={300} /> : <EmptyState message={t('noActivity')} />}
      </ChartCard>
      <ChartCard
        title={t('topActivityTitle')}
        subtitle={t('topActivitySubtitle')}
        action={topRows.length > 0 ? (
          <ExportButton label={t('exportCsv')} onClick={() => exportToCsv('top-companies-activity.csv', columns, topRows)} />
        ) : null}
      >
        <DataTable columns={columns} rows={topRows} emptyMessage={t('noTopCompanies')} />
      </ChartCard>
    </div>
  );
};

SuperActivities.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  period: PropTypes.number.isRequired
};

export default SuperActivities;
